import { useRef, useState } from 'react';

import Button from 'components/Button';
import Carousel, { type CarouselRef } from 'components/Carousel';
import FocusTarget from 'components/FocusTarget';
import { IconCircle } from 'components/Icon';
import Img from 'components/Img';
import PlainButton from 'components/PlainButton';
import Skiplink from 'components/Skiplink';
import ThumbnailList from 'components/ThumbnailList';
import VideoEmbed, { type VideoEmbedAPI } from 'components/VideoEmbed';
import { placeholderImage } from 'graphics';
import type { Product } from 'models/product';
import { getAllImages } from 'utils/business-logic';
import { cn } from 'utils/classNames';
import { is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';
import { clampIndex } from 'utils/math';
import { ENTITIES } from 'utils/string';

import { getVideos, isVideo } from './helpers';
import MediaDialog from './MediaDialog';
import MediaDialogZoomView from './MediaDialogZoomView';

const MEDIUM_SIZE = 810;
// Match the medium size and include the focus outline sized padding the
// carousel has to make it exactly square.
export const MEDIUM_HEIGHT_CLASSES =
	'max-h-[calc(810px+var(--spacing-focus-outline-size,0px)*2)]';
// Should not be needed, but Safari refuses to respect height/max-height 100%.
export const MEDIUM_HEIGHT_IMAGE_CLASSES = 'max-h-[810px]';
export const MEDIUM_MAX_WIDTH_IMAGE_CLASSES = 'max-w-[810px]';

interface Props {
	className?: string;
	dialogLabel: string;
	imageCarouselShowLargeImageButtonLabel: string;
	imagesSkipLinkText: string;
	product: Product;
}

/** Product details primary media section. */
export default function ProductDetailsMedia({
	className,
	dialogLabel,
	imageCarouselShowLargeImageButtonLabel,
	imagesSkipLinkText,
	product,
}: Props) {
	const { t } = useI18n();
	const skipTargetId = 'skip-images';
	const imageCarouselRef = useRef<CarouselRef>(null);
	const videoEmbedRefs = useRef<Map<number, VideoEmbedAPI | null>>(new Map());
	const [isZoomed, setIsZoomed] = useState(false);

	const images = getAllImages(product);
	const videos = getVideos(product);
	const media = [...images.slice(0, 3), ...videos, ...images.slice(3)];

	const mediumImages = media.map((item) =>
		isVideo(item)
			? {
					alt: item.title,
					src: item.largeImageSrc,
					width: item.largeImageWidth,
					height: item.largeImageHeight,
					isVideo: true,
				}
			: {
					alt: item.alt,
					src: item.src,
					width: MEDIUM_SIZE,
					height: MEDIUM_SIZE,
					isVideo: false,
				},
	);

	const thumbnailImages = media.map((item) =>
		isVideo(item)
			? {
					src: item.smallImageSrc,
					width: item.smallImageWidth,
					height: item.smallImageHeight,
					isVideo: true,
				}
			: { src: item.src, isVideo: false },
	);

	const mediaCount = media.length;
	const hasMedia = mediaCount > 0;

	const pauseVideos = ({
		excludingIndex,
	}: { excludingIndex?: number } = {}) => {
		videoEmbedRefs.current.forEach((video, i) => {
			if (is.number(excludingIndex) && i === excludingIndex) {
				return;
			}
			video?.pause?.();
		});
	};

	const [selectedMediumIndex, setSelectedMediumIndex] = useState(0);
	const updateMediumIndex = (index: number) => {
		setSelectedMediumIndex(clampIndex(index, mediaCount));
	};

	const [selectedLargeIndex, setSelectedLargeIndex] = useState(0);
	const updateLargeIndex = (index: number) => {
		setSelectedLargeIndex(clampIndex(index, mediaCount));
	};

	const [isDialogOpen, setIsDialogOpen] = useState(false);
	// ID of video in dialog that should autoplay when ready. Only set on dialog
	// open to play videos that users explicitly click on, don't autoplay videos
	// that users are just navigating through.
	const [autoplayVideoId, setAutoplayVideoId] = useState<string | undefined>(
		undefined,
	);
	const autoplayVideoIndex = autoplayVideoId
		? media.findIndex((m) => isVideo(m) && m.youTubeId === autoplayVideoId)
		: -1;
	const openDialog = (index: number) => {
		setSelectedLargeIndex(index);
		setIsDialogOpen(true);
		if (isVideo(media[index])) {
			setAutoplayVideoId(media[index].youTubeId);
		}
	};
	const closeDialog = () => {
		setIsDialogOpen(false);
		setAutoplayVideoId(undefined);
	};

	const onCarouselPrevNext = () => {
		pauseVideos();
		setAutoplayVideoId(undefined);
	};

	return (
		<>
			<div className={cn('relative', className)}>
				{hasMedia && (
					// Only need a skiplink for the carousel, the placeholder image has
					// no alt text and will thus be practically invisible.
					<>
						<Skiplink
							target={skipTargetId}
							text={imagesSkipLinkText}
							variant="absolute"
						/>
						<div className="relative">
							<Carousel
								ref={imageCarouselRef}
								controlsLayout="inline-floating"
								hasProgressTracker={false}
								controlsSize="md"
								nextButtonLabel={t('general_carousel_next_image_button_label')}
								prevButtonLabel={t(
									'general_carousel_previous_image_button_label',
								)}
								className="relative max-h-full"
								carouselClassName={cn(
									'flex max-h-full min-h-0 gap-x-r-md',
									MEDIUM_HEIGHT_CLASSES,
								)}
								itemClassName="relative flex max-h-full w-full items-center justify-center"
								prevButtonClassName="left-1"
								nextButtonClassName="right-1"
								onNext={() => {
									updateMediumIndex(selectedMediumIndex + 1);
								}}
								onPrevious={() => {
									updateMediumIndex(selectedMediumIndex - 1);
								}}
								onScrollEnd={(items) => {
									// Using isFullyVisible should be enough, but it's a bit flaky.
									const index = items.findIndex(
										(item) => (item.visiblePercentage ?? 0) > 75,
									);
									if (index !== -1) {
										updateMediumIndex(index);
									}
								}}
								items={mediumImages.map((image, i) => (
									<PlainButton
										key={image.src}
										className={cn(
											'group/image-button relative block h-full',
											MEDIUM_MAX_WIDTH_IMAGE_CLASSES,
										)}
										onClick={() => {
											openDialog(i);
										}}
										aria-label={`${imageCarouselShowLargeImageButtonLabel} ${i + 1}`}
										aria-haspopup="dialog"
									>
										<Img
											key={image.src}
											src={image.src}
											alt={image.alt}
											width={image.width}
											height={image.height}
											className={cn(
												'max-h-full object-contain',
												MEDIUM_HEIGHT_IMAGE_CLASSES,
											)}
											// Default from Next is async, which causes flickering on every
											// render in Chrome (e.g. when sliding).
											decoding="sync"
										/>
										{image.isVideo && (
											<IconCircle
												name="play"
												variant="transparent"
												size="md"
												padding="lg"
												className="absolute top-1/2 left-1/2 z-1 -translate-1/2 group-hover/image-button:bg-positive"
											/>
										)}
									</PlainButton>
								))}
							/>
							<Button
								variant="transparent"
								trailingIcon="arrows-expand"
								className="absolute right-2 bottom-focus-outline-size z-10"
								aria-haspopup="dialog"
								onClick={() => {
									openDialog(selectedMediumIndex);
								}}
								aria-label={[
									t('general_image_of_total_text', {
										number: selectedMediumIndex + 1,
										total: mediaCount,
									}),
									imageCarouselShowLargeImageButtonLabel,
								].join(', ')}
							>
								{`${selectedMediumIndex + 1}${ENTITIES.narrowNoBreakSpace}/${ENTITIES.narrowNoBreakSpace}${mediaCount}`}
							</Button>
						</div>
						<ThumbnailList
							images={thumbnailImages}
							hasCustomImageDimensions
							size="md"
							className="mt-2"
							carouselClassName="md:flex-wrap"
							selectedImageIndex={selectedMediumIndex}
							isScreenReaderHidden
							onThumbnailClick={(i) => {
								updateMediumIndex(i);
								imageCarouselRef.current?.goToIndex(i, { animate: false });
							}}
						/>
						<FocusTarget id={skipTargetId} />
					</>
				)}
				{!hasMedia && (
					// Smaller than a regular main image on purpose to get the customer
					// closer to content that actually matters.
					<Img
						src={placeholderImage}
						alt=""
						width={128}
						height={128}
						className="mx-auto size-32"
					/>
				)}
			</div>

			{hasMedia && (
				<MediaDialog
					id="product-images-dialog"
					hasSingleImageFocus={isZoomed}
					isOpen={isDialogOpen}
					label={dialogLabel}
					onClose={closeDialog}
					selectedImageIndex={selectedLargeIndex}
					setSelectedImageIndex={updateLargeIndex}
					thumbnailImages={thumbnailImages}
					hasCustomThumbnailImageDimensions
					onCarouselNext={onCarouselPrevNext}
					onCarouselPrevious={onCarouselPrevNext}
					onCarouselScrollEnd={(index) => {
						pauseVideos({ excludingIndex: index });
						if (autoplayVideoIndex !== -1 && index !== autoplayVideoIndex) {
							setAutoplayVideoId(undefined);
						}
					}}
					items={media.map((item, i) =>
						isVideo(item) ? (
							// Only render the video embed if the dialog is open, otherwise
							// it will load a bunch of YouTube stuff in the background even
							// if the user never opens the dialog.
							<div
								key={item.youTubeId}
								className="flex aspect-video h-full max-w-full items-center justify-center"
							>
								{isDialogOpen && (
									<VideoEmbed
										ref={(ref) => {
											videoEmbedRefs.current.set(i, ref);
										}}
										hasApi
										iframeTitle={item.title}
										youTubeId={item.youTubeId}
										onReady={(video) => {
											if (item.youTubeId === autoplayVideoId) {
												video.play();
											}
										}}
									/>
								)}
							</div>
						) : (
							<MediaDialogZoomView
								key={item.src}
								image={item}
								isZoomed={isZoomed}
								onZoomStart={() => setIsZoomed(true)}
								onZoomStop={() => setIsZoomed(false)}
							/>
						),
					)}
				/>
			)}
		</>
	);
}
ProductDetailsMedia.displayName = 'ProductDetailsMedia';
